import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const languages = [
  { code: 'es', label: 'Español'},
  { code: 'de', label: 'Deutsch'},
  { code: 'en', label: 'English'},
  { code: 'fr', label: 'Français'},
];

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated, user, setUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const currentLang = languages.find(l => i18n.language.startsWith(l.code)) || languages[0];

  // close on outside click or Escape
  useEffect(() => {
    const onClick = (e) => {
      if (!menuRef.current) return;
      if (btnRef.current?.contains(e.target)) return;
      if (!menuRef.current.contains(e.target)) setIsOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const persistClientLang = (lang) => localStorage.setItem('lang', lang);


  const handleSelect = async (code) => {
    if (code === currentLang.code) { setIsOpen(false); return; }
    try {
      await i18n.changeLanguage(code);
    } catch (err) {
      // fallback: still set i18n.language
      i18n.language = code;
    }
    persistClientLang(code);

    if (isAuthenticated) {
      try {
        const { data } = await api.put('/user/language', { language: code });

        // update context user if available
        if (setUser) setUser(prev => ({ ...(prev || {}), language: data.language || code }));
        
      } catch (err) {
        // silent fail: UI already updated and local persisted
        console.error('Failed to save language preference', err);
      }
    }

    setIsOpen(false);
  };

  // Keyboard navigation: open with Enter/Space on button, navigate with Arrow keys
  const onButtonKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(v => !v);
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      // focus first item after menu renders
      setTimeout(() => menuRef.current?.querySelector('button')?.focus(), 0);
    }
  };

  return (
    <div className="relative z-[3001]">
      <button
        ref={btnRef}
        onClick={() => setIsOpen(v => !v)}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-sm font-medium text-gray-700 transition-all shadow-sm active:scale-95"
      >
        <span className="text-lg leading-none">{currentLang.flag}</span>
        <span className="uppercase">{currentLang.code}</span>
        <ChevronDownIcon className={`h-3 w-3 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 cursor-default" onClick={() => setIsOpen(false)} />
          <div
            ref={menuRef}
            role="menu"
            aria-label="Select language"
            className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
          >
            <div className="py-1">
              {languages.map((lang, idx) => {
                const active = i18n.language.startsWith(lang.code);
                return (
                  <button
                    key={lang.code}
                    onClick={() => handleSelect(lang.code)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const next = menuRef.current.querySelectorAll('button')[Math.min(idx + 1, languages.length - 1)];
                        next?.focus();
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const prev = menuRef.current.querySelectorAll('button')[Math.max(idx - 1, 0)];
                        prev?.focus();
                      }
                    }}
                    role="menuitem"
                    className={`
                      w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors
                      ${active ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}
                    `}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    {lang.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSwitcher;
